<template>
  <v-dialog :value="value" max-width="960px" scrollable @input="onDialogInput">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon color="primary">mdi-creation</v-icon>
        <span class="pl-2">AI Insights</span>
        <v-spacer/>
        <v-btn icon aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle v-if="reportSubtitle" class="pb-2">{{ reportSubtitle }}</v-card-subtitle>
      <v-divider/>

      <v-card-text class="pt-4 ai-insights-body">
        <v-alert type="warning" dense text class="mb-4">
          <div class="font-weight-bold text-body-2 mb-1">AI-Generated Content Notice</div>
          <div class="text-body-2">{{ disclaimer }}</div>
        </v-alert>

        <div v-if="loading" class="d-flex flex-column align-center justify-center py-12" data-test="ai-insights-loading">
          <v-progress-circular indeterminate color="primary" size="48" class="mb-4"/>
          <div class="text-h6 grey--text text--darken-2">Analyzing batch…</div>
          <div class="text-body-2 grey--text">This usually takes about a minute.</div>
        </div>

        <v-alert v-else-if="error" type="error" dense text data-test="ai-insights-error">
          <div class="font-weight-bold">Error generating insights</div>
          <div class="text-body-2">{{ error }}</div>
          <v-btn small outlined color="error" class="mt-2" @click="retry">Try again</v-btn>
        </v-alert>

        <v-alert v-else-if="result && result.no_data" type="info" dense text data-test="ai-insights-no-data">
          This batch has no rows to analyze.
        </v-alert>

        <div v-else-if="result" ref="content" class="ai-insights-content" data-test="ai-insights-content">
          <div class="ai-insights-stats">
            <div v-for="tile in statTiles" :key="tile.label" class="ai-insights-stat">
              <div class="ai-insights-stat-label">{{ tile.label }}</div>
              <div class="ai-insights-stat-value">{{ tile.value }}</div>
              <div v-if="tile.detail" class="ai-insights-stat-detail">{{ tile.detail }}</div>
            </div>
          </div>
          <p class="ai-insights-meta">
            <span v-if="generatedAt">Generated {{ generatedAt }}.</span>
            <span v-if="rulesAsOf"> Rules reflect this batch as last processed on {{ rulesAsOf }}.</span>
          </p>
          <div class="ai-insights-report" v-html="renderedInsights"></div>
        </div>
      </v-card-text>

      <v-divider/>
      <v-card-actions class="pa-3">
        <span v-if="showCost && usageText" class="text-caption grey--text text--darken-1" data-test="ai-insights-cost">
          {{ usageText }}
        </span>
        <v-spacer/>
        <v-btn v-if="canExport" color="primary" :loading="exporting" data-test="ai-insights-export" @click="exportPdf">
          <v-icon left>mdi-file-pdf-box</v-icon>
          Export PDF
        </v-btn>
        <v-btn text @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import {
  DISCLAIMER_TEXT,
  buildStatTiles,
  formatTimestamp,
  formatUsage,
  insightsErrorMessage,
  renderInsightsMarkdown,
  slugify
} from '@/utils/aiInsights'

// Owns the request and its cache, like Atlas's useAIInsights hook: opening fetches once, the
// result is kept until cacheKey changes, and a response for an older key is dropped.
export default {
  name: 'AIInsightsModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    // Returns a promise for the API response body.
    fetcher: {
      type: Function,
      required: true
    },
    // Changing this discards the cached result (another batch, store scope, or a save).
    cacheKey: {
      type: String,
      default: ''
    },
    reportTitle: {
      type: String,
      default: 'TPR Batch AI Insights'
    },
    reportSubtitle: {
      type: String,
      default: ''
    },
    pdfName: {
      type: String,
      default: ''
    },
    showCost: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      disclaimer: DISCLAIMER_TEXT,
      result: null,
      error: '',
      loading: false,
      exporting: false,
      requestId: 0
    }
  },
  computed: {
    renderedInsights() {
      return renderInsightsMarkdown(this.result?.insights)
    },
    statTiles() {
      return buildStatTiles(this.result?.stats)
    },
    generatedAt() {
      return formatTimestamp(this.result?.generated_at)
    },
    rulesAsOf() {
      return formatTimestamp(this.result?.stats?.overview?.rules_snapshot_as_of)
    },
    usageText() {
      return formatUsage(this.result)
    },
    canExport() {
      return !this.loading && !this.error && Boolean(this.result?.insights)
    }
  },
  watch: {
    cacheKey() {
      this.requestId += 1
      this.result = null
      this.error = ''
      this.loading = false
      if (this.value) this.load()
    },
    value: {
      handler(open) {
        if (open) this.load()
      },
      immediate: true
    }
  },
  methods: {
    load() {
      if (this.result || this.error || this.loading) return
      this.request()
    },
    retry() {
      this.error = ''
      this.request()
    },
    async request() {
      const id = ++this.requestId
      this.loading = true
      try {
        const data = await this.fetcher()
        if (id !== this.requestId) return
        this.result = data
        this.$emit('generated', data)
      } catch (err) {
        if (id !== this.requestId) return
        console.error('AI Insights request failed:', err)
        this.error = insightsErrorMessage(err)
      } finally {
        if (id === this.requestId) this.loading = false
      }
    },
    onDialogInput(open) {
      if (!open) this.close()
    },
    close() {
      this.$emit('update:value', false)
    },
    // Builds the export with createElement/textContent and clones the rendered report, so no
    // string is re-parsed as HTML (the same approach as Atlas).
    buildPdfDocument() {
      const wrapper = document.createElement('div')
      wrapper.style.cssText = "font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #333; line-height: 1.6; padding: 20px;"

      const titleBlock = document.createElement('div')
      titleBlock.style.cssText = 'text-align: center; margin-bottom: 24px;'
      const heading = document.createElement('h1')
      heading.style.cssText = 'font-size: 24px; margin-bottom: 5px; color: #333;'
      heading.textContent = this.reportTitle
      titleBlock.appendChild(heading)
      if (this.reportSubtitle) {
        const subtitle = document.createElement('p')
        subtitle.style.cssText = 'color: #666; font-size: 14px; margin: 0;'
        subtitle.textContent = this.reportSubtitle
        titleBlock.appendChild(subtitle)
      }
      wrapper.appendChild(titleBlock)

      const notice = document.createElement('div')
      notice.style.cssText = 'background-color: #fffbeb; border: 1px solid #fcd34d; border-radius: 8px; padding: 12px; margin-bottom: 24px;'
      const noticeLabel = document.createElement('p')
      noticeLabel.style.cssText = 'color: #92400e; font-weight: 600; font-size: 12px; margin: 0 0 4px 0;'
      noticeLabel.textContent = 'AI-Generated Content Notice'
      notice.appendChild(noticeLabel)
      const noticeText = document.createElement('p')
      noticeText.style.cssText = 'color: #b45309; font-size: 11px; margin: 0; line-height: 1.5;'
      noticeText.textContent = DISCLAIMER_TEXT
      notice.appendChild(noticeText)
      wrapper.appendChild(notice)

      const content = this.$refs.content.cloneNode(true)
      this.keepSectionsTogether(content)
      wrapper.appendChild(content)
      return wrapper
    },
    // html2pdf's 'avoid-all' won't split an element across pages, so wrapping each "##" heading
    // with the content under it stops a heading being stranded at the bottom of a page.
    keepSectionsTogether(content) {
      const report = content.querySelector('.ai-insights-report')
      if (!report) return
      let section = null
      for (const node of Array.from(report.childNodes)) {
        if (node.nodeName === 'H2') {
          section = document.createElement('div')
          report.insertBefore(section, node)
        }
        if (section) section.appendChild(node)
      }
    },
    async exportPdf() {
      if (!this.$refs.content) return
      // Open the tab now, while the click still counts as a user gesture; rendering takes a few
      // seconds, and a window.open after that is often blocked. Without a tab, download instead.
      const tab = window.open('', '_blank')
      if (tab) {
        tab.opener = null
        tab.document.title = 'Preparing PDF…'
        tab.document.body.textContent = 'Preparing PDF…'
      }
      this.exporting = true
      try {
        const { default: html2pdf } = await import(/* webpackChunkName: "html2pdf" */ 'html2pdf.js')
        const options = {
          margin: [10, 10, 10, 10],
          filename: `${slugify(this.pdfName || this.reportTitle)}-ai-insights-${new Date().toISOString().slice(0, 10)}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
        }
        const worker = html2pdf().set(options).from(this.buildPdfDocument())
        if (tab) {
          tab.location.href = await worker.output('bloburl')
        } else {
          await worker.save()
        }
        this.$emit('exported')
      } catch (err) {
        console.error('AI Insights PDF export failed:', err)
        if (tab) tab.close()
        this.$emit('export-failed', err)
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>
<style scoped>
.ai-insights-body {
  max-height: 70vh;
}

.ai-insights-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 8px;
}

@media (max-width: 700px) {
  .ai-insights-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.ai-insights-stat {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px 10px;
}

.ai-insights-stat-label {
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.ai-insights-stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.ai-insights-stat-detail {
  font-size: 11px;
  color: #777;
}

.ai-insights-meta {
  font-size: 12px;
  color: #777;
  margin-bottom: 16px;
}

.ai-insights-report {
  color: #444;
}

.ai-insights-report >>> h1 {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 6px;
  margin-bottom: 8px;
}

.ai-insights-report >>> h2 {
  font-size: 17px;
  font-weight: 600;
  color: #333;
  margin: 20px 0 8px;
}

.ai-insights-report >>> h3 {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  margin: 0 0 12px;
}

.ai-insights-report >>> p,
.ai-insights-report >>> li {
  font-size: 14px;
  line-height: 1.6;
}

.ai-insights-report >>> ul,
.ai-insights-report >>> ol {
  padding-left: 22px;
  margin-bottom: 12px;
}

.ai-insights-report >>> li {
  margin-bottom: 6px;
}

.ai-insights-report >>> strong {
  color: #333;
}

.ai-insights-report >>> table {
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 12px;
}

.ai-insights-report >>> th,
.ai-insights-report >>> td {
  border: 1px solid #e0e0e0;
  padding: 4px 8px;
  text-align: left;
}
</style>
